import { Hex, Log, toHex, zeroHash } from 'viem'

import { BackendTransaction, TransactionStatus } from '~/types'
import { getReadPublicClient } from '~/utils/requests'

const EIP_5792_RESPONSE_VERSION = '2.0.0'

type CallStatus = {
  version: typeof EIP_5792_RESPONSE_VERSION
  atomic: boolean
  chainId: Hex
  id: string
  status: 100 | 200 | 400 | 500
  receipts: {
    logs: Log[]
    status: '0x1' | '0x0'
    blockHash: Hex
    blockNumber: Hex
    gasUsed: Hex
    transactionHash: Hex
  }[]
}

// EIP-5792 dApps (e.g. Uniswap's pollAllPendingBatches) throw on a failure
// status with no receipts. A zero-block reverted receipt gives them a valid
// shape to render against without claiming any onchain activity.
const buildOffchainFailureReceipt = (): CallStatus['receipts'][number] => ({
  logs: [],
  status: '0x0',
  blockHash: zeroHash,
  blockNumber: toHex(0),
  gasUsed: toHex(0),
  transactionHash: zeroHash,
})

export const getWalletConnectCallStatus = async (
  getTransaction: (transactionId: string) => Promise<BackendTransaction>,
  transactionId: string,
  // Used as response chainId when the transaction can't be looked up; dApps
  // reject responses with `0x0`.
  requestChainId: number,
): Promise<CallStatus> => {
  if (!transactionId) {
    return {
      version: EIP_5792_RESPONSE_VERSION,
      atomic: true,
      chainId: toHex(requestChainId),
      id: '',
      // Spec says 400, but Uniswap ignores 400 and keeps polling forever —
      // only 200/500 unstick the swap card.
      status: 500,
      receipts: [buildOffchainFailureReceipt()],
    }
  }

  let transaction: BackendTransaction

  try {
    transaction = await getTransaction(transactionId)
  } catch {
    // If the transaction is not found, return a 500 status
    return {
      version: EIP_5792_RESPONSE_VERSION,
      atomic: true,
      chainId: toHex(requestChainId),
      id: transactionId,
      status: 500,
      receipts: [buildOffchainFailureReceipt()],
    }
  }

  // When the transaction is created, approved, queued, or submitted, the status is 100 which means the transaction is pending
  if (
    [
      TransactionStatus.Created,
      TransactionStatus.Approved,
      TransactionStatus.Queued,
      TransactionStatus.Submitted,
    ].includes(transaction.status)
  ) {
    return {
      version: EIP_5792_RESPONSE_VERSION,
      atomic: true,
      chainId: toHex(transaction.chainId),
      id: transaction.id,
      status: 100,
      receipts: [],
    }
  }

  // When the transaction is expired or cancelled, the status is 400 which means the transaction won't be executed
  if (
    [TransactionStatus.Expired, TransactionStatus.Cancelled].includes(
      transaction.status,
    )
  ) {
    return {
      version: EIP_5792_RESPONSE_VERSION,
      atomic: true,
      chainId: toHex(transaction.chainId),
      id: transaction.id,
      status: 400,
      receipts: [buildOffchainFailureReceipt()],
    }
  }

  // when transaction cannot be submitted, the status is 400 which means the transaction won't be executed
  if (
    !transaction.transactionHash &&
    transaction.status === TransactionStatus.Failed
  ) {
    return {
      version: EIP_5792_RESPONSE_VERSION,
      atomic: true,
      chainId: toHex(transaction.chainId),
      id: transaction.id,
      status: 400,
      receipts: [buildOffchainFailureReceipt()],
    }
  }

  // when transaction lands onchain
  if (transaction.transactionHash) {
    try {
      const receipt = await getReadPublicClient(
        transaction.chainId,
      ).getTransactionReceipt({
        hash: transaction.transactionHash as Hex,
      })

      return {
        version: EIP_5792_RESPONSE_VERSION,
        atomic: true,
        chainId: toHex(transaction.chainId),
        id: transaction.id,
        status:
          // 200 for when the transaction is successful
          // 500 for when the transaction failed onchain and the transaction hash is available
          transaction.status === TransactionStatus.Succeeded ? 200 : 500,
        receipts: [
          {
            logs: receipt?.logs,
            status: receipt?.status === 'success' ? '0x1' : '0x0',
            blockHash: receipt?.blockHash,
            blockNumber: toHex(receipt?.blockNumber),
            gasUsed: toHex(receipt?.gasUsed),
            transactionHash: receipt?.transactionHash,
          },
        ],
      }

      // eslint-disable-next-line
    } catch {}
  }

  // default to pending
  return {
    version: EIP_5792_RESPONSE_VERSION,
    atomic: true,
    chainId: toHex(transaction.chainId),
    id: transaction.id,
    status: 100,
    receipts: [],
  }
}
